import { supabase } from './supabase';
import { AdCreativeCreditService } from './ad-creative-credits';
import { AdAsset } from './ad-assets';
import { ThumbnailService } from './thumbnail-service';
import { broadcastCreditChange } from './credit-events';
import * as Sentry from '@sentry/react';

export interface AdBrief {
  context: string;
  headline?: string;
  cta?: string;
  stickerText?: string;
  style: string;
  colors?: string[]; // Now optional - AI can choose colors
  platform: string;
  size: string;
}

export interface AdGenerationRequest {
  briefData: AdBrief;
  logoUrl?: string;
  referenceImageUrls?: string[];
  parentGenerationId?: string;
  feedbackData?: {
    feedback: string;
    improvementRequests: string[];
  };
}

export interface AdGenerationResult {
  success: boolean;
  generationId: string;
  generatedImageUrl: string;
  thumbnailUrl?: string;
  revisedPrompt: string;
  originalPrompt: string;
  generationCount: number;
  creditsUsed: number;
  creditsRemaining: number;
  error?: string;
}

export interface AdGeneration {
  id: string;
  user_id: string;
  brief_data: AdBrief;
  logo_url?: string;
  reference_image_urls: string[];
  generated_image_url?: string;
  thumbnail_url?: string;
  generation_count: number;
  parent_generation_id?: string;
  feedback_data?: {
    feedback: string;
    improvementRequests: string[];
  };
  credits_used: number;
  status: 'pending' | 'generating' | 'completed' | 'failed';
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export interface AdGenerationHistory {
  generations: AdGeneration[];
  totalCount: number;
}

// Helper function to convert database Json to AdBrief
function convertJsonToAdBrief(json: any): AdBrief {
  if (typeof json === 'object' && json !== null) {
    return {
      context: String(json.context || ''),
      headline: json.headline ? String(json.headline) : undefined,
      cta: json.cta ? String(json.cta) : undefined,
      stickerText: json.stickerText ? String(json.stickerText) : undefined,
      style: String(json.style || 'Modern'),
      colors: Array.isArray(json.colors) && json.colors.length > 0 
        ? json.colors.map(c => String(c)) 
        : undefined, // Let AI choose colors if not provided
      platform: String(json.platform || 'Facebook'),
      size: String(json.size || 'auto')
    };
  }
  // Fallback if json is not an object
  return {
    context: '',
    headline: undefined,
    cta: undefined,
    stickerText: undefined,
    style: 'Modern',
    colors: undefined, // Let AI choose colors
    platform: 'Facebook',
    size: 'auto'
  };
}

// Helper function to detect if string is base64 data
function isBase64Data(str: string): boolean {
  return str && str.startsWith('data:image/');
}

// Helper function to convert database row to AdGeneration
function convertDbRowToAdGeneration(row: any): AdGeneration {
  return {
    id: row.id,
    user_id: row.user_id,
    brief_data: convertJsonToAdBrief(row.brief_data),
    logo_url: row.logo_url,
    reference_image_urls: Array.isArray(row.reference_image_urls) 
      ? row.reference_image_urls.map(url => String(url))
      : [],
    generated_image_url: row.generated_image_url,
    thumbnail_url: row.thumbnail_url,
    generation_count: row.generation_count || 1,
    parent_generation_id: row.parent_generation_id,
    feedback_data: row.feedback_data || null,
    credits_used: row.credits_used || 1,
    status: row.status || 'completed',
    error_message: row.error_message,
    created_at: row.created_at,
    updated_at: row.updated_at
  };
}

// Simple cache for generation history to prevent redundant requests
interface HistoryCache {
  data: AdGenerationHistory;
  timestamp: number;
  userId: string;
}

let historyCache: HistoryCache | null = null;
const CACHE_DURATION = 30 * 1000; // 30 seconds cache

export class AdGenerationService {
  /**
   * Generate a new ad using OpenAI
   */
  static async generateAd(request: AdGenerationRequest): Promise<AdGenerationResult> {
    try {
      // Check ad-creative credit balance before generation
      const canGenerate = await AdCreativeCreditService.canPerformAdCreativeAction();
      if (!canGenerate.canPerform) {
        throw new Error(canGenerate.message || 'Insufficient ad-creative credits');
      }

      console.log('🎨 Starting ad generation...', request.briefData);

      // Get current user session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('User not authenticated');
      }

      // Call the edge function
      const { data, error } = await supabase.functions.invoke('generate-ad', {
        body: request,
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('❌ Edge function error:', error);
        // Check if it's a credit-related error
        if (error.message?.includes('Insufficient credits') || error.status === 403) {
          throw new Error('Insufficient credits. Please upgrade your plan or purchase credits.');
        }
        throw new Error(error.message || 'Failed to generate ad');
      }

      // Handle edge function response errors
      if (data?.error) {
        console.error('❌ Generation failed:', data.error);
        // Check for specific error types
        if (data.error.includes('Insufficient credits') || data.error.includes('No credits available')) {
          throw new Error('Insufficient credits. Please upgrade your plan or purchase credits.');
        }
        if (data.error.includes('Gemini API key not configured') || data.error.includes('OpenAI API key not configured')) {
          throw new Error('AI service is not properly configured. Please contact support.');
        }
        throw new Error(data.error || 'Generation failed');
      }

      if (!data?.success) {
        console.error('❌ Generation failed with unknown error');
        throw new Error('Failed to generate ad. Please try again.');
      }

      console.log('✅ Ad generation completed successfully');
      
      // Broadcast credit change event for real-time UI updates
      if (data.creditsUsed && data.creditsRemaining !== undefined) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          broadcastCreditChange(
            'credit_consumed',
            user.id,
            -data.creditsUsed,
            data.creditsRemaining,
            {
              reason: 'Ad Generation',
              transactionId: data.generationId
            }
          );
        }
      }
      
      // Clear cache since we have new generation data
      this.clearHistoryCache();
      
      return data as AdGenerationResult;

    } catch (error) {
      console.error('❌ Ad generation error:', error);
      Sentry.captureException(error, {
        tags: { operation: 'ad_generation', component: 'AdGenerationService' },
        contexts: {
          adGeneration: {
            briefData: request.briefData,
            hasLogo: !!request.logoUrl,
            referenceImagesCount: request.referenceImageUrls?.length || 0,
            isRegeneration: !!request.parentGenerationId
          }
        }
      });
      throw error;
    }
  }

  /**
   * Regenerate an ad with feedback and optional new assets using dedicated regeneration endpoint
   */
  static async regenerateAd(
    originalGenerationId: string,
    feedback: string,
    improvementRequests: string[],
    newLogo?: AdAsset | null,
    newReferenceImages?: AdAsset[]
  ): Promise<AdGenerationResult> {
    try {
      // Check ad-creative credit balance before regeneration
      const canGenerate = await AdCreativeCreditService.canPerformAdCreativeAction();
      if (!canGenerate.canPerform) {
        throw new Error(canGenerate.message || 'Insufficient ad-creative credits');
      }

      // Get the original generation
      const originalGeneration = await this.getGeneration(originalGenerationId);
      if (!originalGeneration) {
        throw new Error('Original generation not found');
      }

      // Intelligently merge assets
      // Logo: Use new logo if provided, otherwise use original
      const logoUrl = newLogo?.asset_url || originalGeneration.logo_url;
      
      // Reference images: Merge new references with original (excluding the original generated image since it will be the base for editing)
      const referenceImageUrls = [
        // Add any new reference images (higher priority)
        ...(newReferenceImages?.map(img => img.asset_url) || []),
        // Add original reference images (lower priority, avoid duplicates)
        ...(originalGeneration.reference_image_urls || [])
      ].filter((url, index, array) => array.indexOf(url) === index); // Remove duplicates

      const regenerationRequest = {
        originalGenerationId: originalGenerationId,
        briefData: originalGeneration.brief_data,
        logoUrl: logoUrl,
        referenceImageUrls: referenceImageUrls,
        feedbackData: {
          feedback,
          improvementRequests
        }
      };

      console.log('🔄 Regenerating ad with feedback using dedicated endpoint:', feedback);
      console.log('🎨 Asset merge summary:', {
        originalLogo: !!originalGeneration.logo_url,
        newLogo: !!newLogo,
        usingLogo: logoUrl,
        originalReferences: originalGeneration.reference_image_urls?.length || 0,
        newReferences: newReferenceImages?.length || 0,
        totalReferences: referenceImageUrls.length
      });

      // Get current user session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('User not authenticated');
      }

      // Call the dedicated regenerate-ad edge function
      const { data, error } = await supabase.functions.invoke('regenerate-ad', {
        body: regenerationRequest,
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('❌ Regeneration edge function error:', error);
        // Check if it's a credit-related error
        if (error.message?.includes('Insufficient credits') || error.status === 403) {
          throw new Error('Insufficient credits. Please upgrade your plan or purchase credits.');
        }
        throw new Error(error.message || 'Failed to regenerate ad');
      }

      // Handle edge function response errors
      if (data?.error) {
        console.error('❌ Regeneration failed:', data.error);
        // Check for specific error types
        if (data.error.includes('Insufficient credits') || data.error.includes('No credits available')) {
          throw new Error('Insufficient credits. Please upgrade your plan or purchase credits.');
        }
        if (data.error.includes('Gemini API key not configured') || data.error.includes('OpenAI API key not configured')) {
          throw new Error('AI service is not properly configured. Please contact support.');
        }
        throw new Error(data.error || 'Regeneration failed');
      }

      if (!data?.success) {
        console.error('❌ Regeneration failed with unknown error');
        throw new Error('Failed to regenerate ad. Please try again.');
      }

      console.log('✅ Ad regeneration completed successfully using image editing');
      
      // Broadcast credit change event for real-time UI updates
      if (data.creditsUsed && data.creditsRemaining !== undefined) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          broadcastCreditChange(
            'credit_consumed',
            user.id,
            -data.creditsUsed,
            data.creditsRemaining,
            {
              reason: 'Ad Regeneration',
              transactionId: data.generationId
            }
          );
        }
      }
      
      // Clear cache since we have new generation data
      this.clearHistoryCache();
      
      return data as AdGenerationResult;

    } catch (error) {
      console.error('❌ Ad regeneration error:', error);
      Sentry.captureException(error, {
        tags: { operation: 'ad_regeneration', component: 'AdGenerationService' },
        contexts: {
          adRegeneration: {
            originalGenerationId,
            feedback,
            improvementRequestsCount: improvementRequests.length,
            hasNewLogo: !!newLogo,
            newReferenceImagesCount: newReferenceImages?.length || 0
          }
        }
      });
      throw error;
    }
  }

  /**
   * Get a specific generation by ID (team-aware)
   */
  static async getGeneration(generationId: string): Promise<AdGeneration | null> {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .rpc('get_team_aware_ad_generation_by_id', {
          generation_id: generationId,
          calling_user_id: user.id
        });

      if (error) {
        console.error('❌ Error fetching generation:', error);
        return null;
      }

      // Extract the single generation from array
      const generationData = data?.[0] || null;
      if (!generationData) {
        return null;
      }

      return convertDbRowToAdGeneration(generationData);
    } catch (error) {
      console.error('❌ Error in getGeneration:', error);
      Sentry.captureException(error, {
        tags: { operation: 'get_generation', component: 'AdGenerationService' },
        contexts: {
          generation: {
            generationId
          }
        }
      });
      return null;
    }
  }

  /**
   * Get user's ad generation history - optimized for performance with caching
   */
  static async getGenerationHistory(
    limit: number = 20,
    offset: number = 0
  ): Promise<AdGenerationHistory> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Check if user is a team member and get organization owner if so
      // We need to do this first to determine the correct cache key
      const { data: userData } = await supabase
        .from('users')
        .select('organization_id, team_role')
        .eq('id', user.id)
        .single();

      let targetUserId = user.id;
      if (userData?.organization_id && userData?.team_role) {
        // Check if this user is a team member (not organization owner)
        const { data: orgData } = await supabase
          .from('organizations')
          .select('created_by')
          .eq('id', userData.organization_id)
          .single();
        
        if (orgData?.created_by && orgData.created_by !== user.id) {
          // This is a team member - show organization owner's generations
          targetUserId = orgData.created_by;
          console.log('🔄 Team member detected - showing organization owner generation history:', {
            teamMember: user.id,
            organizationOwner: targetUserId
          });
        }
      }

      // Check cache for requests - now using correct target user ID
      if (offset === 0 && historyCache) {
        const now = Date.now();
        const isValidCache = (
          historyCache.userId === targetUserId &&
          (now - historyCache.timestamp) < CACHE_DURATION
        );
        
        if (isValidCache) {
          // If we have enough cached items to satisfy the request
          if (historyCache.data.generations.length >= limit) {
            console.log(`📦 Using cached generation history for user: ${targetUserId} (${limit} of ${historyCache.data.generations.length} cached)`);
            // Return only the requested number of items from cache
            return {
              generations: historyCache.data.generations.slice(0, limit),
              totalCount: historyCache.data.totalCount
            };
          } else {
            // We need to fetch more data - clear cache to force fresh fetch
            console.log(`🗑️ Cache has insufficient data (${historyCache.data.generations.length} < ${limit}), clearing cache`);
            historyCache = null;
          }
        }
      }

      const startTime = Date.now();

      let generationsResult;
      let countResult = { count: null, error: null };

      // Use RPC function if user is a team member or if we're querying a different user
      if (targetUserId !== user.id) {
        console.log('🔑 Using team-aware RPC function for ad generations');
        const { data, error } = await supabase.rpc('get_team_aware_ad_generations', {
          user_limit: limit,
          user_offset: offset
        });

        generationsResult = { data, error };
        
        // Extract count from the first record if available
        if (data && data.length > 0) {
          countResult = { count: data[0].total_count, error: null };
        } else {
          countResult = { count: 0, error: null };
        }
      } else {
        // Direct query for organization owners or users without team roles
        generationsResult = await supabase
          .from('ad_generations')
          .select(`
            id,
            brief_data,
            logo_url,
            reference_image_urls,
            generated_image_url,
            thumbnail_url,
            generation_count,
            parent_generation_id,
            feedback_data,
            credits_used,
            status,
            error_message,
            created_at,
            updated_at
          `)
          .eq('user_id', targetUserId)
          .order('created_at', { ascending: false })
          .range(offset, offset + limit - 1);

        // Get count only for first page and only if the main query succeeded
        if (offset === 0 && !generationsResult.error) {
          countResult = await supabase
            .from('ad_generations')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', targetUserId);
        }
      }

      const queryTime = Date.now() - startTime;

      if (generationsResult.error) {
        console.error('❌ Error fetching generation history:', generationsResult.error);
        throw generationsResult.error;
      }

      if (countResult.error && offset === 0) {
        console.warn('⚠️ Error fetching generation count:', countResult.error);
        // Don't throw on count error, just log warning
      }

      const generations = (generationsResult.data || []).map(row => ({
        ...convertDbRowToAdGeneration(row),
        user_id: targetUserId // Use target user ID for the actual owner
      }));

      // Check for missing thumbnails and generate them in background
      this.generateMissingThumbnails(generations);


      const result = {
        generations,
        totalCount: countResult.count || generations.length
      };

      // Cache results - expand cache for larger requests
      if (offset === 0) {
        // For first page, always cache
        historyCache = {
          data: result,
          timestamp: Date.now(),
          userId: targetUserId // Cache by target user (organization owner for team members)
        };
      } else if (historyCache && historyCache.userId === targetUserId) {
        // For subsequent pages, append to existing cache if it's the same user
        const now = Date.now();
        if ((now - historyCache.timestamp) < CACHE_DURATION) {
          // Merge new results with existing cache
          const existingGenerations = historyCache.data.generations;
          const newGenerations = result.generations.filter(gen => 
            !existingGenerations.some(existing => existing.id === gen.id)
          );
          
          historyCache = {
            data: {
              generations: [...existingGenerations, ...newGenerations],
              totalCount: result.totalCount
            },
            timestamp: historyCache.timestamp, // Keep original timestamp
            userId: targetUserId
          };
          console.log(`💾 Expanded cached generation history for user: ${targetUserId} (${historyCache.data.generations.length} total items)`);
        }
      }

      return result;

    } catch (error) {
      console.error('❌ Error in getGenerationHistory:', error);
      
      // Handle specific timeout errors gracefully
      if (error.message?.includes('timeout') || error.message?.includes('statement timeout') || error.code === '57014') {
        console.warn('🕐 Database query timed out, returning empty results');
        Sentry.captureException(error, {
          tags: { operation: 'get_generation_history_timeout', component: 'AdGenerationService' },
          level: 'warning',
          contexts: {
            generationHistory: {
              limit,
              offset,
              errorType: 'timeout'
            }
          }
        });
        return {
          generations: [],
          totalCount: 0
        };
      }
      
      Sentry.captureException(error, {
        tags: { operation: 'get_generation_history', component: 'AdGenerationService' },
        contexts: {
          generationHistory: {
            limit,
            offset
          }
        }
      });
      throw error;
    }
  }

  /**
   * Clear the generation history cache - call this after creating new generations
   */
  static clearHistoryCache(): void {
    historyCache = null;
    console.log('🗑️ Cleared generation history cache');
  }

  /**
   * Get generation chains (original + all regenerations)
   */
  static async getGenerationChain(generationId: string): Promise<AdGeneration[]> {
    try {
      // First, find the root generation (no parent)
      let rootId = generationId;
      const currentGeneration = await this.getGeneration(generationId);
      
      if (currentGeneration?.parent_generation_id) {
        rootId = currentGeneration.parent_generation_id;
      }

      // Get all generations in the chain
      const { data, error } = await supabase
        .from('ad_generations')
        .select('*')
        .or(`id.eq.${rootId},parent_generation_id.eq.${rootId}`)
        .order('generation_count', { ascending: true });

      if (error) {
        console.error('❌ Error fetching generation chain:', error);
        throw error;
      }

      return data.map(row => convertDbRowToAdGeneration(row));
    } catch (error) {
      console.error('❌ Error in getGenerationChain:', error);
      Sentry.captureException(error, {
        tags: { operation: 'get_generation_chain', component: 'AdGenerationService' },
        contexts: {
          generationChain: {
            generationId
          }
        }
      });
      throw error;
    }
  }

  /**
   * Delete a generation and its children
   */
  static async deleteGeneration(generationId: string): Promise<void> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get the generation to verify ownership
      const generation = await this.getGeneration(generationId);
      if (!generation) {
        throw new Error('Generation not found');
      }

      if (generation.user_id !== user.id) {
        throw new Error('Unauthorized to delete this generation');
      }

      // Delete the generation (cascading delete will handle children)
      const { error } = await supabase
        .from('ad_generations')
        .delete()
        .eq('id', generationId)
        .eq('user_id', user.id);

      if (error) {
        console.error('❌ Error deleting generation:', error);
        throw error;
      }

      console.log('✅ Generation deleted successfully');
    } catch (error) {
      console.error('❌ Error in deleteGeneration:', error);
      Sentry.captureException(error, {
        tags: { operation: 'delete_generation', component: 'AdGenerationService' },
        contexts: {
          deleteGeneration: {
            generationId
          }
        }
      });
      throw error;
    }
  }

  /**
   * Get generation statistics for the user
   */
  static async getGenerationStats(): Promise<{
    totalGenerations: number;
    thisMonth: number;
    creditsUsed: number;
    favoriteStyle: string;
    favoritePlatform: string;
  }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get total generations
      const { count: totalGenerations } = await supabase
        .from('ad_generations')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      // Get this month's generations
      const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
      const { count: thisMonth } = await supabase
        .from('ad_generations')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .gte('created_at', startOfMonth);

      // Get credits used (sum of all generations)
      const { data: creditData } = await supabase
        .from('ad_generations')
        .select('credits_used')
        .eq('user_id', user.id);

      const creditsUsed = creditData?.reduce((sum, gen) => sum + gen.credits_used, 0) || 0;

      // Get favorite style and platform
      const { data: generations } = await supabase
        .from('ad_generations')
        .select('brief_data')
        .eq('user_id', user.id);

      let favoriteStyle = 'Modern';
      let favoritePlatform = 'Facebook';

      if (generations && generations.length > 0) {
        const styles: { [key: string]: number } = {};
        const platforms: { [key: string]: number } = {};

        generations.forEach(gen => {
          const brief = gen.brief_data as AdBrief;
          styles[brief.style] = (styles[brief.style] || 0) + 1;
          platforms[brief.platform] = (platforms[brief.platform] || 0) + 1;
        });

        favoriteStyle = Object.keys(styles).reduce((a, b) => styles[a] > styles[b] ? a : b, 'Modern');
        favoritePlatform = Object.keys(platforms).reduce((a, b) => platforms[a] > platforms[b] ? a : b, 'Facebook');
      }

      return {
        totalGenerations: totalGenerations || 0,
        thisMonth: thisMonth || 0,
        creditsUsed,
        favoriteStyle,
        favoritePlatform
      };

    } catch (error) {
      console.error('❌ Error in getGenerationStats:', error);
      Sentry.captureException(error, {
        tags: { operation: 'get_generation_stats', component: 'AdGenerationService' }
      });
      return {
        totalGenerations: 0,
        thisMonth: 0,
        creditsUsed: 0,
        favoriteStyle: 'Modern',
        favoritePlatform: 'Facebook'
      };
    }
  }

  /**
   * Validate ad brief data
   */
  static validateBrief(brief: Partial<AdBrief>): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Context is now required instead of headline/CTA
    if (!brief.context || brief.context.trim().length === 0) {
      errors.push('Creative brief/context is required');
    } else if (brief.context.length < 50) {
      errors.push('Context must be at least 50 characters');
    } else if (brief.context.length > 1000) {
      errors.push('Context must be 1000 characters or less');
    }

    // Optional text fields - only validate if provided
    if (brief.headline && brief.headline.length > 100) {
      errors.push('Headline must be 100 characters or less');
    }

    if (brief.cta && brief.cta.length > 50) {
      errors.push('Call-to-Action must be 50 characters or less');
    }

    if (brief.stickerText && brief.stickerText.length > 30) {
      errors.push('Sticker text must be 30 characters or less');
    }

    if (!brief.style) {
      errors.push('Style is required');
    }

    // Colors are now optional - AI can choose appropriate colors if not provided
    if (brief.colors && brief.colors.length > 0 && brief.colors.some(color => !color || color.trim() === '')) {
      errors.push('If colors are provided, they cannot be empty');
    }

    if (!brief.platform) {
      errors.push('Platform is required');
    }

    if (!brief.size) {
      errors.push('Size/format is required');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Generate missing thumbnails for existing generations (background task)
   */
  static async generateMissingThumbnails(generations: AdGeneration[]): Promise<void> {
    try {
      const generationsWithoutThumbnails = generations.filter(
        gen => gen.generated_image_url && !gen.thumbnail_url && gen.status === 'completed'
      );

      if (generationsWithoutThumbnails.length === 0) {
        return;
      }

      console.log(`🖼️ Found ${generationsWithoutThumbnails.length} generations without thumbnails, generating in background...`);

      // Process thumbnails in background (don't await)
      generationsWithoutThumbnails.forEach(async (generation) => {
        try {
          const thumbnailUrl = await ThumbnailService.createAndUploadThumbnailFromUrl(
            generation.generated_image_url!,
            `gen_${generation.id}`,
            { width: 600, height: 600, quality: 100 }
          );

          // Update the database with the thumbnail URL
          await supabase
            .from('ad_generations')
            .update({ thumbnail_url: thumbnailUrl })
            .eq('id', generation.id);

          console.log(`✅ Generated thumbnail for generation ${generation.id}`);

          // Clear cache so next load will include the thumbnail
          this.clearHistoryCache();

        } catch (error) {
          console.error(`❌ Failed to generate thumbnail for generation ${generation.id}:`, error);
          Sentry.captureException(error, {
            tags: { operation: 'generate_thumbnail', component: 'AdGenerationService' },
            contexts: {
              thumbnail: {
                generationId: generation.id,
                hasImageUrl: !!generation.generated_image_url
              }
            }
          });
        }
      });

    } catch (error) {
      console.error('❌ Error in generateMissingThumbnails:', error);
      Sentry.captureException(error, {
        tags: { operation: 'generate_missing_thumbnails', component: 'AdGenerationService' },
        contexts: {
          thumbnails: {
            generationsCount: generations.length
          }
        }
      });
    }
  }

  /**
   * Get recommended improvements based on generation performance
   */
  static getRecommendedImprovements(generation: AdGeneration): string[] {
    const improvements: string[] = [];
    const brief = generation.brief_data;

    // Style-based recommendations
    if (brief.style === 'Minimalist') {
      improvements.push('Add more visual elements to increase engagement');
      improvements.push('Consider using bolder colors to stand out');
    } else if (brief.style === 'Bold') {
      improvements.push('Ensure readability by balancing bold elements');
      improvements.push('Consider adding white space for better focus');
    }

    // Platform-based recommendations
    if (brief.platform.toLowerCase().includes('facebook')) {
      improvements.push('Reduce text overlay to improve organic reach');
      improvements.push('Add engaging visual elements to stop scroll');
    } else if (brief.platform.toLowerCase().includes('google')) {
      improvements.push('Ensure clear value proposition is visible');
      improvements.push('Add trust signals to improve performance');
    }

    // Generic recommendations
    improvements.push('Try different color combinations');
    improvements.push('Experiment with different layouts');
    improvements.push('Test alternative headlines');

    return improvements;
  }

  /**
   * Migrate base64 data to file storage for a single generation
   */
  static async migrateGenerationToFileStorage(generationId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log(`🔄 Migrating generation ${generationId} to file storage...`);

      // Get the generation
      const generation = await this.getGeneration(generationId);
      if (!generation) {
        return { success: false, error: 'Generation not found' };
      }

      // Check if already migrated (URL instead of base64)
      if (!generation.generated_image_url || !isBase64Data(generation.generated_image_url)) {
        console.log(`✅ Generation ${generationId} already migrated or has no image`);
        return { success: true };
      }

      // Upload base64 to storage
      const { StorageService } = await import('./storage');
      const uploadResult = await StorageService.uploadGeneratedAd(
        generation.generated_image_url,
        generationId
      );

      if (uploadResult.error) {
        console.error(`❌ Failed to upload generation ${generationId}:`, uploadResult.error);
        return { success: false, error: uploadResult.error };
      }

      // Update database record
      const { error: updateError } = await supabase
        .from('ad_generations')
        .update({
          generated_image_url: uploadResult.url,
          thumbnail_url: uploadResult.thumbnailUrl || generation.thumbnail_url
        })
        .eq('id', generationId);

      if (updateError) {
        console.error(`❌ Failed to update generation ${generationId}:`, updateError);
        return { success: false, error: updateError.message };
      }

      console.log(`✅ Successfully migrated generation ${generationId}`);
      
      // Clear cache to reflect changes
      this.clearHistoryCache();
      
      return { success: true };

    } catch (error) {
      console.error(`❌ Error migrating generation ${generationId}:`, error);
      Sentry.captureException(error, {
        tags: { operation: 'migrate_generation', component: 'AdGenerationService' },
        contexts: {
          migration: {
            generationId
          }
        }
      });
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Migrate all base64 data to file storage (batch operation)
   */
  static async migrateAllGenerationsToFileStorage(batchSize: number = 5): Promise<{
    totalProcessed: number;
    successCount: number;
    failureCount: number;
    errors: string[];
  }> {
    try {
      console.log('🚀 Starting migration of all generations to file storage...');

      // Get all generations with base64 data
      const { data: generations, error } = await supabase
        .from('ad_generations')
        .select('id, generated_image_url')
        .not('generated_image_url', 'is', null);

      if (error) {
        throw error;
      }

      // Filter to only base64 data
      const base64Generations = generations?.filter(gen => 
        gen.generated_image_url && isBase64Data(gen.generated_image_url)
      ) || [];

      console.log(`📊 Found ${base64Generations.length} generations with base64 data to migrate`);

      if (base64Generations.length === 0) {
        return {
          totalProcessed: 0,
          successCount: 0,
          failureCount: 0,
          errors: []
        };
      }

      let totalProcessed = 0;
      let successCount = 0;
      let failureCount = 0;
      const errors: string[] = [];

      // Process in batches
      for (let i = 0; i < base64Generations.length; i += batchSize) {
        const batch = base64Generations.slice(i, i + batchSize);
        console.log(`📦 Processing batch ${Math.floor(i/batchSize) + 1}/${Math.ceil(base64Generations.length/batchSize)} (${batch.length} items)`);

        const batchPromises = batch.map(gen => this.migrateGenerationToFileStorage(gen.id));
        const results = await Promise.all(batchPromises);

        results.forEach((result, index) => {
          totalProcessed++;
          if (result.success) {
            successCount++;
          } else {
            failureCount++;
            errors.push(`Generation ${batch[index].id}: ${result.error}`);
          }
        });

        // Small delay between batches to avoid overwhelming the system
        if (i + batchSize < base64Generations.length) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      console.log(`✅ Migration completed: ${successCount}/${totalProcessed} successful`);

      return {
        totalProcessed,
        successCount,
        failureCount,
        errors
      };

    } catch (error) {
      console.error('❌ Error in migration process:', error);
      Sentry.captureException(error, {
        tags: { operation: 'migrate_all_generations', component: 'AdGenerationService' },
        contexts: {
          migration: {
            batchSize
          }
        }
      });
      throw error;
    }
  }
}

export default AdGenerationService;